import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import {
  DatePicker
  // , Select, Skeleton
} from 'antd'
import { connect } from 'react-redux'
import Actions from '../../../actions'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import { debounce, reduce } from 'lodash'
import { isBrowser, isTablet } from 'react-device-detect'
import { colors } from '@lmc/common/constants'
import { gaEvent } from '../../../common/services/analytics'
import ListHeaderSearch from '../../../LEGACY/ListHeaderSearch'
import { LogsTable } from './components'
import {
  PropertyFilters,
  PropertyFilterTags
} from '../../../components/filters'
import dayjs from 'dayjs'
import { Button, RangePicker, Icon } from '@lmc/ui-kit'
import qs from 'qs'
import { defaultDateRanges } from '../../../common/constants'
import { DownloadModal } from 'screens/Reports/DownloadCentre/components'
import { CSV_EXPORT_TYPE } from 'screens/Reports/DownloadCentre/components/utils'
import DownloadLogsCSVButton from './components/DownloadLogsCSVButton'
import { commonPatterns } from '../../../utils/enhancedTestIds'

const isDesktop = isBrowser || isTablet
const FETCH_DEBOUNCE = 500
const MODAL_ID = 'addLog'
const SEARCH_BOX_PLACEHOLDER = 'Search by title or description'

const getQuery = () =>
  window.location.search && qs.parse(window.location.search.replace('?', ''))

export class LogsDashboard extends PureComponent {
  constructor(props) {
    super(props)
    const query = getQuery()
    const dateTimeFormat = query.isPreciseDateTime ? undefined : 'DD-MM-YYYY'
    const startOfDay = query.isPreciseDateTime ? undefined : 'day'
    const endOfDay = query.isPreciseDateTime ? undefined : 'day'

    this.state = {
      timeLogged: {
        from: query.from
          ? dayjs(query.from, dateTimeFormat).startOf(startOfDay)
          : dayjs().subtract(1, 'month').startOf('day'),
        to: query.to
          ? dayjs(query.to, dateTimeFormat).endOf(endOfDay)
          : dayjs().endOf('day')
      },
      search: '',
      isModalOpen: false
    }

    this.openModal = this.openModal.bind(this)
    this.closeModal = this.closeModal.bind(this)
  }

  componentDidMount() {
    const query = getQuery()
    if (Object.keys(query).length) {
      // clear
      this.props.clearFilters()
      const { resident, item, category, items, loggedBy } = query
      const filterId = 'logs'

      if (resident) {
        this.props.toggleActiveFilter({
          filterId,
          key: 'resident',
          value: resident
        })
      }
      if (item) {
        this.props.toggleActiveFilter({
          filterId,
          key: 'item',
          value: item
        })
      }
      if (items) {
        this.props.toggleActiveFilter({
          filterId,
          key: 'item',
          value: items
        })
      }
      if (category) {
        this.props.toggleActiveFilter({
          filterId,
          key: 'category',
          value: category
        })
      }
      if (loggedBy) {
        this.props.toggleActiveFilter({
          filterId,
          key: 'loggedBy',
          value: loggedBy
        })
      }
    }
    this.fetchLogs()
  }

  gaEvent = ({ label, action }) =>
    gaEvent({
      category: 'Logs Dashboard',
      action,
      label
    })

  fetchLogs = () => {
    const { search, timeLogged } = this.state
    const withFormattedDates = reduce(
      timeLogged,
      (result, value, key) => {
        if (value) result[key] = value.toISOString()
        return result
      },
      {}
    )
    this.props.fetchLogs({ search, timeLogged: withFormattedDates })
  }

  fetchLogsCsv = ({ allLogs }) => {
    const { search, timeLogged } = this.state
    const withFormattedDates = reduce(
      timeLogged,
      (result, value, key) => {
        if (value) result[key] = value.toISOString()
        return result
      },
      {}
    )
    this.props.fetchLogsCsv({ search, timeLogged: withFormattedDates, allLogs })
  }

  debounceFetchLogs = debounce(
    () => {
      this.fetchLogs()
    },
    FETCH_DEBOUNCE,
    {}
  )

  handleFilterChange = ({ key, value }) => {
    const { goToFirstPage, toggleActiveFilter, activeFilters } = this.props
    goToFirstPage()
    if (key === 'doneBySu' && !activeFilters?.[key]?.includes(value)) {
      this.props.clearSelectedFilter('doneBySu')
    }
    toggleActiveFilter({ key, value })
    this.debounceFetchLogs()
    this.gaEvent({
      action: 'Set Filter',
      label: key
    })
  }

  handleOpenModal = () => {
    window.scrollTo(0, 0)
    this.gaEvent({ action: 'Open Add Log modal' })
    this.props.openModal()
  }

  handleSearchChange = (e) => {
    const newValue = e.target.value
    this.setState({ search: newValue }, () => {
      this.props.goToFirstPage()
      this.debounceFetchLogs()
      this.gaEvent({
        action: 'Text Search'
      })
    })
  }

  handleSearchClear = () => {
    this.setState({ search: '' }, () => {
      this.props.goToFirstPage()
      this.fetchLogs()
    })
  }

  handleTimeLoggedChange = ([startDate, endDate]) => {
    let timeLogged = {}
    if (startDate) timeLogged = { from: dayjs(startDate).startOf('day') }
    if (endDate) timeLogged = { ...timeLogged, to: dayjs(endDate).endOf('day') }
    // CARE-1312: clearing the date filter sets it 6 months into the past
    if (!startDate && !endDate) {
      timeLogged = {
        from: dayjs().startOf('day').subtract(6, 'month')
      }
    }

    this.setState({ timeLogged }, () => {
      this.props.goToFirstPage()
      this.fetchLogs()
    })
    this.gaEvent({ action: 'Date Search' })
  }

  renderHeaderButtons = () => {
    const { exporting, loading } = this.props

    const csvDisabled = exporting || loading
    return (
      <div style={styles.buttonsContainer}>
        <DownloadLogsCSVButton
          openModal={this.openModal}
          disabled={csvDisabled}
          exporting={exporting}
          testId="logs-dashboard"
        />
        <Button
          onClick={() => this.handleOpenModal()}
          style={styles.headerButton}
          variant="primary"
          icon={<Icon name="PlusOutlined" />}
          data-cy="add-log"
          data-testid={commonPatterns.button('logs-dashboard', 'add')}
        >
          Add Log
        </Button>
      </div>
    )
  }

  renderSearch = () => (
    <ListHeaderSearch
      handleChange={this.handleSearchChange}
      handleClear={this.handleSearchClear}
      inputStyle={styles.searchInput}
      placeholder={SEARCH_BOX_PLACEHOLDER}
      style={styles.searchWrapper}
      value={this.state.search}
      data-testid={commonPatterns.input('logs-dashboard', 'search')}
    />
  )

  renderDatePicker = () => {
    const { timeLogged } = this.state

    return (
      <div
        style={styles.datePicker}
        data-testid={commonPatterns.input('logs-dashboard', 'date-range')}
      >
        <RangePicker
          presets={defaultDateRanges}
          format="DD-MM-YYYY"
          defaultValue={[timeLogged?.from, timeLogged?.to]}
          disabledDate={(date) => date.isAfter(dayjs())}
          onChange={(dates) => {
            if (!dates) return null
            this.handleTimeLoggedChange(dates)
            return null
          }}
        />
      </div>
    )
  }

  renderFilters = () => {
    const { activeFilters, allFilters } = this.props
    return (
      <PropertyFilters
        activeFilters={activeFilters}
        allFilters={allFilters}
        expandedView={isDesktop}
        onChange={this.handleFilterChange}
        style={styles.propertyFilters}
        testId="logs-dashboard"
      />
    )
  }

  renderFilterTags = () => {
    const { activeFilters, allFilters } = this.props
    return (
      <PropertyFilterTags
        style={styles.filterTags}
        activeFilters={activeFilters}
        allFilters={allFilters}
        onChange={this.handleFilterChange}
        testId="logs-dashboard"
      />
    )
  }

  openModal() {
    this.setState({ isModalOpen: true })
  }

  closeModal() {
    this.setState({ isModalOpen: false })
  }

  render() {
    const {
      role,
      preferredPeopleNominative,
      preferredCarePlansNominative,
      activeFilters
    } = this.props
    const mappedFilters = {
      logCategories: activeFilters?.category,
      logCategoryItems: activeFilters?.item,
      residents: activeFilters?.resident,
      carers: activeFilters?.loggedBy,
      dateRange: Object.values(this.state.timeLogged)
    }

    return (
      <div style={styles.wrapper} data-testid="logs-dashboard">
        {!isDesktop && this.renderHeaderButtons()}
        {!isDesktop && this.renderSearch()}
        {!isDesktop && this.renderDatePicker()}
        {isDesktop && (
          <div style={styles.topContainer}>
            <div style={styles.searchInputParentContainer}>
              {this.renderSearch()}
              <div style={styles.filtersParentContainer}>
                {this.renderDatePicker()}
                {this.renderFilters()}
              </div>
            </div>
            <div style={styles.addLogButtonContainer}>
              {this.renderHeaderButtons()}
            </div>
            <DownloadModal
              isOpen={this.state.isModalOpen}
              onClose={this.closeModal}
              CSVExportType={CSV_EXPORT_TYPE.LOGS}
              preferredPeopleNominative={preferredPeopleNominative}
              preferredCarePlansNominative={preferredCarePlansNominative}
              formValues={{ ...mappedFilters }}
            />
          </div>
        )}
        <div>{this.renderFilterTags()}</div>
        <LogsTable
          onChange={this.fetchLogs}
          role={role}
          testId="logs-dashboard-table"
        />
      </div>
    )
  }
}

const styles = {
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: isDesktop ? null : 24,
    position: 'relative'
  },
  datePicker: {
    padding: 5
  },
  dropdownContainer: {
    position: 'relative'
  },
  dropdownMenu: {
    // left: isDesktop ? -6 : null,
    position: 'absolute',
    right: isDesktop ? null : 0,
    top: 40
  },
  exportButton: {
    color: colors.black,
    textShadow: 'none'
  },
  filtersParentContainer: {
    display: 'flex',
    flexFlow: 'row wrap',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: 10,
    marginBottom: 10,
    alignItems: 'center'
  },
  headerButton: {
    marginLeft: 6
  },
  propertyFilters: {
    padding: 5
  },
  addLogButtonContainer: {
    width: '40%'
  },
  searchInputParentContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '60%'
  },
  searchInput: {
    fontSize: 16
  },
  searchWrapper: {
    paddingLeft: 5,
    maxWidth: isDesktop ? 760 : 600
  },
  topContainer: {
    alignItems: 'flex-start',
    display: 'flex'
  },
  wrapper: {
    margin: '24px 24px 50px'
  },
  filterTags: { paddingBottom: 12 }
}

LogsDashboard.propTypes = {
  activeFilters: PropTypes.object,
  allFilters: PropTypes.object,
  fetchLogs: PropTypes.func.isRequired,
  goToFirstPage: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  toggleActiveFilter: PropTypes.func.isRequired,
  preferredPeopleNominative: PropTypes.string,
  preferredCarePlansNominative: PropTypes.string,
  loading: PropTypes.bool,
  role: PropTypes.string,
  exporting: PropTypes.bool
}

const mapStateToProps = (state) => ({
  activeFilters: state.global.filters.logs,
  role: state.global.user.role,
  allFilters: Selectors.getLogFilters(state),
  exporting: state.logs.exporting,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  preferredCarePlansNominative: state.global.home.preferredCarePlansNominative,
  loading: state.global.loading
})

const mapDispatchToProps = (dispatch) => ({
  fetchLogs: ({ search, timeLogged }) =>
    dispatch(
      Operations.fetchLogs({
        search,
        timeLogged
      })
    ),
  fetchLogsCsv: ({ search, timeLogged, allLogs }) =>
    dispatch(
      Operations.fetchLogsCsv(
        {
          search,
          timeLogged
        },
        allLogs
      )
    ),
  goToFirstPage: () => dispatch(Actions.setLogsPageNumber(1)),
  openModal: () => {
    dispatch(Actions.openModal({ modalId: MODAL_ID }))
    dispatch(Actions.addForm({ formId: MODAL_ID }))
  },
  clearFilters: () => {
    const filterId = 'logs'
    dispatch(Actions.resetFilter({ filterId }))
  },
  clearSelectedFilter: (filterKey) => {
    const filterId = 'logs'
    dispatch(Actions.resetSelectedFilter({ filterId, filterKey }))
  },
  toggleActiveFilter: ({ key, value }) =>
    dispatch(
      Actions.toggleActiveFilter({
        filterId: 'logs',
        key,
        value
      })
    )
})

export default connect(mapStateToProps, mapDispatchToProps)(LogsDashboard)
